/**
 * Single source of truth for application roles and role-based routing.
 *
 * Antes: o mapa `role → rota` estava duplicado em `useAuth.ts`
 * (ROLE_DASHBOARD_ROUTES), em `Router.tsx` (rolePaths) e a checagem de role
 * espalhada por RouteGuard + os três hooks de layout — quatro lugares para
 * uma decisão de segurança.
 *
 * Agora: todos importam daqui.
 */

export type AppRole =
  | "developer"
  | "super_admin"
  | "admin"
  | "professor"
  | "aluno";

export const APP_ROLES: readonly AppRole[] = [
  "developer",
  "super_admin",
  "admin",
  "professor",
  "aluno",
] as const;

/** Rotas de entrada do sistema */
export const LOGIN_ROUTE = "/";
export const ROUTER_ROUTE = "/router";
export const CHANGE_PASSWORD_ROUTE = "/alterar-senha";
export const SETUP_2FA_ROUTE = "/configurar-2fa";
export const VERIFY_2FA_ROUTE = "/verificar-2fa";
export const SELECT_ACADEMY_ROUTE = "/selecionar_academia";

/** Dashboard de destino por role — ÚNICO mapa do sistema */
export const ROLE_DASHBOARD_ROUTES: Record<AppRole, string> = {
  developer: "/dev_dashboard",
  super_admin: "/superadmin_dashboard",
  admin: "/admin_dashboard",
  professor: "/prof_dashboard",
  aluno: "/aluno_dashboard",
};

/** Roles privilegiadas: exigem 2FA (AAL2) e sofrem idle timeout */
export const PRIVILEGED_ROLES: readonly AppRole[] = ["developer", "super_admin"] as const;

export function isAppRole(value: unknown): value is AppRole {
  return typeof value === "string" && (APP_ROLES as readonly string[]).includes(value);
}

export function isPrivilegedRole(role: unknown): boolean {
  return isAppRole(role) && (PRIVILEGED_ROLES as readonly string[]).includes(role);
}

/** Rota de dashboard da role, ou `null` se a role for desconhecida */
export function roleDashboard(role: unknown): string | null {
  return isAppRole(role) ? ROLE_DASHBOARD_ROUTES[role] : null;
}

/**
 * Regra única de autorização de rota.
 * `developer` tem acesso universal (bypass de suporte já existente no RouteGuard).
 */
export function canAccess(role: unknown, allowedRoles: readonly AppRole[]): boolean {
  if (!isAppRole(role)) return false;
  if (role === "developer") return true;
  return allowedRoles.includes(role);
}
